// src/pages/Plantation/PlantationPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Leaf, TreePine, Scale, BarChart3, Plus, Check, RefreshCw,
  ChevronRight, ChevronDown, Edit2, Trash2, X, AlertCircle, Truck, MapPin, Shield,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import toast from '../../utils/toast';
import AppelMatinalTab from './tabs/AppelMatinalTab';
import VehiculesTab from './tabs/VehiculesTab';
import TransportTab from './tabs/TransportTab';
import SureteTab from './tabs/SureteTab';
import OperationsTab from './tabs/OperationsTab';
import PointageRegimesTab from './tabs/PointageRegimesTab';
import SuiviBudgetaireTab from './tabs/SuiviBudgetaireTab';
import {
  plantationService,
  PlantationParcelle,
  PlantationCampagne,
  PlantationBonPesee,
  PlantationBordereau,
  RendementStats,
  PlantationVehicule,
} from '../../services/plantation.service';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) => (n || 0).toLocaleString('fr-FR').replace(/ /g, ' ');
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtFCFA = (n: number) => fmt(n) + ' FCFA';

const STATUT_BON_BADGE: Record<string, string> = {
  brouillon: 'bg-gray-100 text-gray-600',
  valide:    'bg-emerald-100 text-emerald-700',
  integre:   'bg-blue-100 text-blue-700',
};

const STATUT_BORDEREAU_BADGE: Record<string, string> = {
  brouillon: 'bg-gray-100 text-gray-600',
  valide:    'bg-blue-100 text-blue-700',
  paye:      'bg-emerald-100 text-emerald-700',
  cloture:   'bg-indigo-100 text-indigo-700',
};

const SOURCE_BADGE: Record<string, string> = {
  manuel:       'bg-amber-100 text-amber-700',
  pont_bascule: 'bg-purple-100 text-purple-700',
  mobile:       'bg-sky-100 text-sky-700',
};

const NIVEAU_COULEUR: Record<string, string> = {
  plantation: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  secteur:    'bg-emerald-50 border-emerald-200 text-emerald-800',
  bloc:       'bg-amber-50 border-amber-200 text-amber-800',
  parcelle:   'bg-lime-50 border-lime-200 text-lime-800',
};

const CULTURE_LABEL: Record<string, string> = {
  palmier_huile: 'Palmier à huile',
  hevea:         'Hévéa',
  cacao:         'Cacao',
  coton:         'Coton',
  autre:         'Autre',
};

const PRODUIT_LABEL: Record<string, string> = {
  regime_palmier: 'Régime palmier',
  latex:          'Latex',
  feves_cacao:    'Fèves cacao',
  coton_graine:   'Coton graine',
  autre:          'Autre',
};

// ── Composant noeud d'arbre ────────────────────────────────────────────────────

interface TreeNodeProps {
  node: PlantationParcelle;
  depth: number;
  onSelect: (p: PlantationParcelle) => void;
  selectedId: string | null;
  onAdd: (parent: PlantationParcelle) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, depth, onSelect, selectedId, onAdd }) => {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = (node.children || []).length > 0;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors group
          ${isSelected ? 'bg-emerald-100' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        {hasChildren ? (
          <button
            className="p-0.5 rounded text-gray-400 hover:text-gray-600 flex-shrink-0"
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 ${NIVEAU_COULEUR[node.type] || ''}`}>
          {node.type.slice(0, 3).toUpperCase()}
        </span>
        <span className="font-medium text-gray-800 text-sm truncate">{node.code}</span>
        <span className="text-gray-500 text-xs truncate">{node.nom}</span>
        {node.surface != null && (
          <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{node.surface} ha</span>
        )}
        {node.type !== 'parcelle' && (
          <button
            className="ml-1 p-0.5 rounded text-gray-300 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition flex-shrink-0"
            title="Ajouter un enfant"
            onClick={e => { e.stopPropagation(); onAdd(node); }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && hasChildren && (
        <div>
          {(node.children || []).map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1}
              onSelect={onSelect} selectedId={selectedId} onAdd={onAdd} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Modal Nouvelle Parcelle ────────────────────────────────────────────────────

interface ModalParcelleProps {
  parent?: PlantationParcelle | null;
  entiteId: string;
  onClose: () => void;
  onSaved: () => void;
  editing?: PlantationParcelle | null;
}

const childType = (parentType: string): PlantationParcelle['type'] => {
  const map: Record<string, PlantationParcelle['type']> = {
    plantation: 'secteur', secteur: 'bloc', bloc: 'parcelle',
  };
  return map[parentType] || 'parcelle';
};

const ModalParcelle: React.FC<ModalParcelleProps> = ({ parent, entiteId, onClose, onSaved, editing }) => {
  const [form, setForm] = useState<Partial<PlantationParcelle>>(editing ?? {
    type: parent ? childType(parent.type) : 'plantation',
    parentId: parent?.id,
    typeCulture: parent?.typeCulture || 'palmier_huile',
    statut: 'actif',
    entiteId,
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!form.code || !form.nom) { toast.error('Code et nom obligatoires'); return; }
    setLoading(true);
    try {
      if (editing) {
        await plantationService.updateParcelle(editing.id, form);
        toast.success('Parcelle mise à jour');
      } else {
        await plantationService.createParcelle({ ...form, entiteId });
        toast.success('Parcelle créée');
      }
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const set = (k: keyof PlantationParcelle, v: any) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {editing ? 'Modifier la parcelle' : `Nouvelle ${form.type || 'entité'}`}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          {parent && (
            <p className="text-xs text-gray-500">
              Parent : <strong>{parent.code} — {parent.nom}</strong>
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Code *</label>
              <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.code || ''} onChange={e => set('code', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
              <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.nom || ''} onChange={e => set('nom', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type de culture</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.typeCulture || 'palmier_huile'} onChange={e => set('typeCulture', e.target.value)}>
                {Object.entries(CULTURE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Variété</label>
              <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.variete || ''} onChange={e => set('variete', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Surface (ha)</label>
              <input type="number" step="0.01" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.surface ?? ''} onChange={e => set('surface', parseFloat(e.target.value) || undefined)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Année plantation</label>
              <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.annePlantation ?? ''} onChange={e => set('annePlantation', parseInt(e.target.value) || undefined)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.statut || 'actif'} onChange={e => set('statut', e.target.value)}>
              <option value="actif">Actif</option>
              <option value="jachère">Jachère</option>
              <option value="reconversion">Reconversion</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Latitude</label>
              <input type="number" step="0.000001" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.latitude ?? ''} onChange={e => set('latitude', parseFloat(e.target.value) || undefined)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Longitude</label>
              <input type="number" step="0.000001" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.longitude ?? ''} onChange={e => set('longitude', parseFloat(e.target.value) || undefined)} />
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal Nouveau Bon de Pesée ─────────────────────────────────────────────────

interface ModalBonProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  arbre: PlantationParcelle[];
  onClose: () => void;
  onSaved: () => void;
}

const flattenParcelles = (nodes: PlantationParcelle[]): PlantationParcelle[] => {
  const result: PlantationParcelle[] = [];
  const walk = (list: PlantationParcelle[]) => {
    list.forEach(n => {
      result.push(n);
      if (n.children) walk(n.children);
    });
  };
  walk(nodes);
  return result;
};

const ModalBon: React.FC<ModalBonProps> = ({ entiteId, campagnes, arbre, onClose, onSaved }) => {
  const allParcelles = flattenParcelles(arbre).filter(p => p.type === 'parcelle');
  const [form, setForm] = useState({
    campagneId: campagnes.find(c => c.statut === 'en_cours')?.id || '',
    parcelleId: '',
    journalierId: '',
    datePesee: new Date().toISOString().slice(0, 10),
    heurePesee: '',
    produit: 'regime_palmier' as PlantationBonPesee['produit'],
    poidsBrut: '',
    poidsTare: '',
    tauxHumidite: '',
  });
  const [loading, setLoading] = useState(false);

  const poidsBrut = parseFloat(form.poidsBrut) || 0;
  const poidsTare = parseFloat(form.poidsTare) || 0;
  const poidsNet = Math.max(0, poidsBrut - poidsTare);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.datePesee || !form.produit || poidsBrut <= 0) {
      toast.error('Date, produit et poids brut sont obligatoires');
      return;
    }
    setLoading(true);
    try {
      await plantationService.createBon({
        entiteId,
        campagneId: form.campagneId || undefined,
        parcelleId: form.parcelleId || undefined,
        journalierId: form.journalierId || undefined,
        datePesee: form.datePesee,
        heurePesee: form.heurePesee || undefined,
        produit: form.produit,
        poidsBrut,
        poidsTare,
        poidsNet,
        tauxHumidite: form.tauxHumidite ? parseFloat(form.tauxHumidite) : undefined,
        source: 'manuel',
        statut: 'brouillon',
      });
      toast.success('Bon de pesée créé');
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur lors de la création du bon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Nouveau bon de pesée</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Campagne</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.campagneId} onChange={e => set('campagneId', e.target.value)}>
                <option value="">— Aucune —</option>
                {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Parcelle</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.parcelleId} onChange={e => set('parcelleId', e.target.value)}>
                <option value="">— Toutes —</option>
                {allParcelles.map(p => <option key={p.id} value={p.id}>{p.code} — {p.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.datePesee} onChange={e => set('datePesee', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Heure</label>
              <input type="time" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.heurePesee} onChange={e => set('heurePesee', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Produit *</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.produit} onChange={e => set('produit', e.target.value)}>
              {Object.entries(PRODUIT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Poids brut (kg) *</label>
              <input type="number" step="0.1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.poidsBrut} onChange={e => set('poidsBrut', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tare (kg)</label>
              <input type="number" step="0.1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.poidsTare} onChange={e => set('poidsTare', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Poids net</label>
              <div className="w-full border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2 text-sm font-semibold text-emerald-800">
                {fmt(poidsNet)} kg
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Taux humidité (%)</label>
            <input type="number" step="0.1" max="100" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.tauxHumidite} onChange={e => set('tauxHumidite', e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {loading ? 'Enregistrement…' : 'Créer le bon'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal Générer Bordereau ────────────────────────────────────────────────────

interface ModalBordereauProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  arbre: PlantationParcelle[];
  onClose: () => void;
  onSaved: () => void;
}

const ModalBordereau: React.FC<ModalBordereauProps> = ({ entiteId, campagnes, arbre, onClose, onSaved }) => {
  const allParcelles = flattenParcelles(arbre).filter(p => p.type === 'parcelle');
  const [form, setForm] = useState({
    campagneId: campagnes.find(c => c.statut === 'en_cours')?.id || '',
    periode: new Date().toISOString().slice(0, 7),
    parcelleId: '',
    tauxUnitaire: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.periode || !form.tauxUnitaire) {
      toast.error('Période et taux unitaire obligatoires');
      return;
    }
    setLoading(true);
    try {
      await plantationService.createBordereau({
        entiteId,
        campagneId: form.campagneId || undefined,
        periode: form.periode,
        parcelleId: form.parcelleId || undefined,
        tauxUnitaire: parseFloat(form.tauxUnitaire),
      });
      toast.success('Bordereau généré');
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur lors de la génération du bordereau');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Générer un bordereau récolte</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Campagne</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.campagneId} onChange={e => set('campagneId', e.target.value)}>
              <option value="">— Toutes —</option>
              {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Période (AAAA-MM) *</label>
            <input type="month" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.periode} onChange={e => set('periode', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Parcelle (vide = toutes)</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.parcelleId} onChange={e => set('parcelleId', e.target.value)}>
              <option value="">— Toutes les parcelles —</option>
              {allParcelles.map(p => <option key={p.id} value={p.id}>{p.code} — {p.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Taux unitaire (FCFA/kg) *</label>
            <input type="number" step="1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.tauxUnitaire} onChange={e => set('tauxUnitaire', e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {loading ? 'Génération…' : 'Générer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal Paiement Bordereau ───────────────────────────────────────────────────

interface ModalPaiementProps {
  bordereau: PlantationBordereau;
  onClose: () => void;
  onSaved: () => void;
}

const ModalPaiement: React.FC<ModalPaiementProps> = ({ bordereau, onClose, onSaved }) => {
  const [form, setForm] = useState({
    modePaiement: 'especes',
    datePaiement: new Date().toISOString().slice(0, 10),
    reference: '',
    signataire: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handlePay = async () => {
    if (!form.modePaiement || !form.datePaiement) {
      toast.error('Mode et date de paiement obligatoires');
      return;
    }
    setLoading(true);
    try {
      await plantationService.payerBordereau(bordereau.id, {
        modePaiement: form.modePaiement,
        datePaiement: form.datePaiement,
        reference: form.reference || undefined,
        signataire: form.signataire || undefined,
      });
      toast.success('Bordereau payé');
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Payer le bordereau {bordereau.numero}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-3">
          <div className="bg-emerald-50 rounded-lg px-4 py-3 text-center">
            <p className="text-xs text-gray-500">Montant à payer</p>
            <p className="text-xl font-bold text-emerald-700">
              {fmtFCFA(bordereau.totalPoidsNet * bordereau.tauxUnitaire)}
            </p>
            <p className="text-xs text-gray-400">{fmt(bordereau.totalPoidsNet)} kg × {fmt(bordereau.tauxUnitaire)} FCFA/kg</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.modePaiement} onChange={e => set('modePaiement', e.target.value)}>
              <option value="especes">Espèces</option>
              <option value="virement">Virement bancaire</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="cheque">Chèque</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de paiement</label>
            <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.datePaiement} onChange={e => set('datePaiement', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Référence / N° pièce</label>
            <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.reference} onChange={e => set('reference', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Signataire</label>
            <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.signataire} onChange={e => set('signataire', e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handlePay} disabled={loading}
            className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {loading ? 'Paiement…' : 'Confirmer le paiement'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── KPI Card ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 truncate">{label}</p>
      <p className="text-lg font-bold text-gray-800 leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  </div>
);

// ── Page principale ────────────────────────────────────────────────────────────

const TAB_IDS = ['parcellaire', 'appel_matinal', 'operations', 'pointage_regimes', 'pesees', 'budget', 'bordereaux', 'rendements', 'vehicules', 'transport', 'surete'] as const;
type TabId = typeof TAB_IDS[number];

// Mapping path segment → tabId
const PATH_TO_TAB: Record<string, TabId> = {
  parcelles:        'parcellaire',
  campagnes:        'parcellaire', // fallback to parcellaire, campagnes shown via tab
  'pointage-regimes': 'pointage_regimes',
  'bons-pesee':     'pesees',
  bordereaux:       'bordereaux',
  transport:        'transport',
  budget:           'budget',
  vehicules:        'vehicules',
  surete:           'surete',
  operations:       'operations',
  'appel-matinal':  'appel_matinal',
};

const PlantationPage: React.FC = () => {
  const { user } = useAuth();
  const entiteId: string = (user as any)?.scopeEntiteId || (user as any)?.entiteId || '';
  const location = useLocation();

  const tabFromPath = useMemo<TabId>(() => {
    const segment = location.pathname.replace(/^\/plantation\/?/, '').split('/')[0];
    return PATH_TO_TAB[segment] ?? (location.search.match(/[?&]tab=(\w+)/)?.[1] as TabId) ?? 'parcellaire';
  }, [location]);

  const [activeTab, setActiveTab] = useState<TabId>(tabFromPath);

  useEffect(() => { setActiveTab(tabFromPath); }, [tabFromPath]);

  // Data
  const [vehicules, setVehicules] = useState<PlantationVehicule[]>([]);
  const [arbre, setArbre] = useState<PlantationParcelle[]>([]);
  const [campagnes, setCampagnes] = useState<PlantationCampagne[]>([]);
  const [bons, setBons] = useState<PlantationBonPesee[]>([]);
  const [bordereaux, setBordereaux] = useState<PlantationBordereau[]>([]);
  const [rendements, setRendements] = useState<RendementStats[]>([]);

  // Loading states
  const [loadingArbre, setLoadingArbre] = useState(false);
  const [loadingBons, setLoadingBons] = useState(false);
  const [loadingBordereaux, setLoadingBordereaux] = useState(false);
  const [loadingRendements, setLoadingRendements] = useState(false);

  // Modals
  const [showModalParcelle, setShowModalParcelle] = useState(false);
  const [parentForParcelle, setParentForParcelle] = useState<PlantationParcelle | null>(null);
  const [editingParcelle, setEditingParcelle] = useState<PlantationParcelle | null>(null);
  const [selectedParcelle, setSelectedParcelle] = useState<PlantationParcelle | null>(null);
  const [showModalBon, setShowModalBon] = useState(false);
  const [showModalBordereau, setShowModalBordereau] = useState(false);
  const [bordereauAPayer, setBordereauAPayer] = useState<PlantationBordereau | null>(null);

  // Filtres pesées
  const [filtreCampagne, setFiltreCampagne] = useState('');
  const [filtreStatutBon, setFiltreStatutBon] = useState('');
  const [filtreParcelleId, setFiltreParcelleId] = useState('');
  const [filtreDate, setFiltreDate] = useState('');

  // Rendements
  const [groupBy, setGroupBy] = useState<'parcelle' | 'bloc' | 'secteur'>('parcelle');
  const [campagneRendement, setCampagneRendement] = useState('');

  // Campagne sélectionnée (header)
  const [campagneActive, setCampagneActive] = useState('');

  // Load arbre + campagnes au montage
  const loadArbre = useCallback(async () => {
    if (!entiteId) return;
    setLoadingArbre(true);
    try {
      const data = await plantationService.getArbre(entiteId);
      setArbre(data);
    } catch {
      toast.error('Erreur chargement parcellaire');
    } finally {
      setLoadingArbre(false);
    }
  }, [entiteId]);

  const loadCampagnes = useCallback(async () => {
    if (!entiteId) return;
    try {
      const data = await plantationService.listCampagnes(entiteId);
      setCampagnes(data);
      const actif = data.find(c => c.statut === 'en_cours');
      if (actif) { setCampagneActive(actif.id); setCampagneRendement(actif.id); }
    } catch { /* silencieux */ }
  }, [entiteId]);

  const loadBons = useCallback(async () => {
    if (!entiteId) return;
    setLoadingBons(true);
    try {
      const data = await plantationService.listBons({
        entiteId,
        campagneId: filtreCampagne || undefined,
        parcelleId: filtreParcelleId || undefined,
        statut: filtreStatutBon || undefined,
        dateDebut: filtreDate || undefined,
      });
      setBons(data);
    } catch {
      toast.error('Erreur chargement bons de pesée');
    } finally {
      setLoadingBons(false);
    }
  }, [entiteId, filtreCampagne, filtreParcelleId, filtreStatutBon, filtreDate]);

  const loadBordereaux = useCallback(async () => {
    if (!entiteId) return;
    setLoadingBordereaux(true);
    try {
      const data = await plantationService.listBordereaux({ entiteId, campagneId: campagneActive || undefined });
      setBordereaux(data);
    } catch {
      toast.error('Erreur chargement bordereaux');
    } finally {
      setLoadingBordereaux(false);
    }
  }, [entiteId, campagneActive]);

  const loadRendements = useCallback(async () => {
    if (!entiteId) return;
    setLoadingRendements(true);
    try {
      const data = await plantationService.getRendements({
        entiteId,
        campagneId: campagneRendement || undefined,
        groupBy,
      });
      setRendements(data);
    } catch {
      toast.error('Erreur chargement rendements');
    } finally {
      setLoadingRendements(false);
    }
  }, [entiteId, campagneRendement, groupBy]);

  useEffect(() => { loadArbre(); loadCampagnes(); }, [loadArbre, loadCampagnes]);
  useEffect(() => {
    if (!entiteId) return;
    plantationService.listVehicules(entiteId).then(setVehicules).catch(() => {});
  }, [entiteId]);
  useEffect(() => { if (activeTab === 'pesees') loadBons(); }, [activeTab, loadBons]);
  useEffect(() => { if (activeTab === 'bordereaux') loadBordereaux(); }, [activeTab, loadBordereaux]);
  useEffect(() => { if (activeTab === 'rendements') loadRendements(); }, [activeTab, loadRendements]);

  // Sync pont-bascule
  const handleSync = async () => {
    try {
      const res = await plantationService.syncPontBascule(entiteId, campagneActive || undefined);
      toast.success(`${res.importes} bon(s) importé(s), ${res.ignores} ignoré(s)`);
      loadBons();
    } catch {
      toast.error('Erreur synchronisation pont-bascule');
    }
  };

  // Valider un bon
  const handleValiderBon = async (id: string) => {
    try {
      await plantationService.validerBon(id);
      toast.success('Bon validé');
      loadBons();
    } catch {
      toast.error('Erreur validation');
    }
  };

  // KPIs parcellaire
  const allFlat = flattenParcelles(arbre);
  const totalSurface = allFlat.reduce((s, p) => s + (p.surface || 0), 0);
  const nbParcelles = allFlat.filter(p => p.type === 'parcelle').length;
  const surfaceActive = allFlat.filter(p => p.statut === 'actif').reduce((s, p) => s + (p.surface || 0), 0);

  // KPIs rendements
  const totalRecolte = rendements.reduce((s, r) => s + r.totalKg, 0);
  const moyRendement = rendements.length > 0
    ? rendements.reduce((s, r) => s + r.rendementTha, 0) / rendements.length
    : 0;
  const tauxObjectif = rendements.filter(r => r.objectifTha).length > 0
    ? rendements.filter(r => r.objectifTha).reduce((s, r) => s + (r.tauxRealisationPct || 0), 0) /
      rendements.filter(r => r.objectifTha).length
    : 0;

  const chartData = rendements.map(r => ({
    name: r.parcelle?.code || r.parcelleId.slice(0, 8),
    rendement: parseFloat(r.rendementTha.toFixed(2)),
    objectif: r.objectifTha ? parseFloat(r.objectifTha.toFixed(2)) : undefined,
  }));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Gestion Plantation</h1>
            <p className="text-xs text-gray-500">Parcellaire · Appel matinal · Opérations · Pointage régimes · Pesées · Budget · Bordereaux · Rendements · Véhicules · Transport · Sûreté</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={campagneActive}
            onChange={e => setCampagneActive(e.target.value)}
          >
            <option value="">Toutes les campagnes</option>
            {campagnes.map(c => (
              <option key={c.id} value={c.id}>
                {c.libelle} ({c.statut === 'en_cours' ? 'En cours' : c.statut === 'planifiee' ? 'Planifiée' : 'Clôturée'})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto">
          {([
            { id: 'parcellaire', label: 'Parcellaire', icon: TreePine },
            { id: 'appel_matinal', label: 'Appel matinal', icon: Check },
            { id: 'operations', label: 'Opérations', icon: Leaf },
            { id: 'pointage_regimes', label: 'Pointage régimes', icon: Scale },
            { id: 'pesees', label: 'Pesées usine', icon: Scale },
            { id: 'budget', label: 'Budget', icon: BarChart3 },
            { id: 'bordereaux', label: 'Bordereaux récolte', icon: Leaf },
            { id: 'rendements', label: 'Rendements', icon: BarChart3 },
            { id: 'vehicules', label: 'Véhicules', icon: Truck },
            { id: 'transport', label: 'Transport', icon: MapPin },
            { id: 'surete', label: 'Sûreté', icon: Shield },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Parcellaire ── */}
      {activeTab === 'parcellaire' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <KpiCard label="Surface totale" value={`${fmt(totalSurface)} ha`} icon={TreePine} color="bg-emerald-100 text-emerald-700" />
            <KpiCard label="Nb parcelles" value={nbParcelles} icon={Leaf} color="bg-lime-100 text-lime-700" />
            <KpiCard label="Surface active" value={`${fmt(surfaceActive)} ha`} icon={Check} color="bg-teal-100 text-teal-700" />
          </div>

          <div className="flex gap-4">
            {/* Arbre */}
            <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h2 className="font-semibold text-gray-800 text-sm">Arborescence parcellaire</h2>
                <div className="flex gap-2">
                  <button onClick={loadArbre}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title="Rafraîchir">
                    <RefreshCw className={`w-4 h-4 ${loadingArbre ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => { setParentForParcelle(null); setEditingParcelle(null); setShowModalParcelle(true); }}
                    className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Nouvelle plantation
                  </button>
                </div>
              </div>
              <div className="p-3 overflow-y-auto max-h-[60vh]">
                {arbre.length === 0 && !loadingArbre && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    <TreePine className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Aucune unité parcellaire définie
                  </div>
                )}
                {arbre.map(node => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    onSelect={p => setSelectedParcelle(p)}
                    selectedId={selectedParcelle?.id || null}
                    onAdd={parent => {
                      setParentForParcelle(parent);
                      setEditingParcelle(null);
                      setShowModalParcelle(true);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Panneau détail */}
            {selectedParcelle && (
              <div className="w-80 bg-white rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 text-sm truncate">{selectedParcelle.code} — {selectedParcelle.nom}</h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingParcelle(selectedParcelle); setParentForParcelle(null); setShowModalParcelle(true); }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setSelectedParcelle(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  <div className="flex gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded border ${NIVEAU_COULEUR[selectedParcelle.type] || ''}`}>
                      {selectedParcelle.type}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      selectedParcelle.statut === 'actif' ? 'bg-emerald-100 text-emerald-700'
                      : selectedParcelle.statut === 'jachère' ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'}`}>
                      {selectedParcelle.statut}
                    </span>
                  </div>
                  {[
                    { label: 'Culture', value: CULTURE_LABEL[selectedParcelle.typeCulture] || selectedParcelle.typeCulture },
                    { label: 'Variété', value: selectedParcelle.variete },
                    { label: 'Surface', value: selectedParcelle.surface != null ? `${selectedParcelle.surface} ha` : undefined },
                    { label: 'Année plantation', value: selectedParcelle.annePlantation },
                    { label: 'Latitude', value: selectedParcelle.latitude },
                    { label: 'Longitude', value: selectedParcelle.longitude },
                  ].map(({ label, value }) => value != null && (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-medium text-gray-800">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab Appel Matinal ── */}
      {activeTab === 'appel_matinal' && (
        <AppelMatinalTab
          entiteId={entiteId}
          campagnes={campagnes}
          parcelles={allFlat}
        />
      )}

      {/* ── Tab Opérations ── */}
      {activeTab === 'operations' && (
        <OperationsTab
          entiteId={entiteId}
          campagnes={campagnes}
          parcelles={allFlat}
          campagneActive={campagneActive}
        />
      )}

      {/* ── Tab Pointage Régimes ── */}
      {activeTab === 'pointage_regimes' && (
        <PointageRegimesTab
          entiteId={entiteId}
          campagnes={campagnes}
          parcelles={allFlat}
          campagneActive={campagneActive}
        />
      )}

      {/* ── Tab Budget ── */}
      {activeTab === 'budget' && (
        <SuiviBudgetaireTab
          entiteId={entiteId}
          campagnes={campagnes}
          campagneActive={campagneActive}
        />
      )}

      {/* ── Tab Pesées ── */}
      {activeTab === 'pesees' && (
        <div className="space-y-4">
          {/* Filtres */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Campagne</label>
              <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                value={filtreCampagne} onChange={e => setFiltreCampagne(e.target.value)}>
                <option value="">Toutes</option>
                {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Statut</label>
              <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                value={filtreStatutBon} onChange={e => setFiltreStatutBon(e.target.value)}>
                <option value="">Tous</option>
                <option value="brouillon">Brouillon</option>
                <option value="valide">Validé</option>
                <option value="integre">Intégré</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date</label>
              <input type="date" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                value={filtreDate} onChange={e => setFiltreDate(e.target.value)} />
            </div>
            <button onClick={loadBons}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingBons ? 'animate-spin' : ''}`} /> Filtrer
            </button>
            <div className="ml-auto flex gap-2">
              <button onClick={handleSync}
                className="px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 flex items-center gap-1">
                <RefreshCw className="w-3.5 h-3.5" /> Sync pont-bascule
              </button>
              <button onClick={() => setShowModalBon(true)}
                className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Nouveau bon
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    {['Numéro', 'Date', 'Parcelle', 'Produit', 'Poids brut', 'Poids net', 'Source', 'Statut', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loadingBons ? (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Chargement…</td></tr>
                  ) : bons.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Aucun bon de pesée</td></tr>
                  ) : bons.map(bon => (
                    <tr key={bon.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{bon.numero}</td>
                      <td className="px-3 py-2.5 text-gray-700">{fmtDate(bon.datePesee)}</td>
                      <td className="px-3 py-2.5 text-gray-600 text-xs">{bon.parcelle?.code || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-700">{PRODUIT_LABEL[bon.produit] || bon.produit}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700">{fmt(bon.poidsBrut)} kg</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{fmt(bon.poidsNet)} kg</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${SOURCE_BADGE[bon.source] || 'bg-gray-100 text-gray-600'}`}>
                          {bon.source.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUT_BON_BADGE[bon.statut] || 'bg-gray-100 text-gray-600'}`}>
                          {bon.statut}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {bon.statut === 'brouillon' && (
                          <button onClick={() => handleValiderBon(bon.id)}
                            className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Valider
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Bordereaux ── */}
      {activeTab === 'bordereaux' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-gray-700">Bordereaux de récolte</h2>
            <div className="flex gap-2">
              <button onClick={loadBordereaux}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
                <RefreshCw className={`w-4 h-4 ${loadingBordereaux ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={() => setShowModalBordereau(true)}
                className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Générer bordereau
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    {['Numéro', 'Période', 'Parcelle', 'Nb bons', 'Poids net (kg)', 'Taux/kg', 'Montant', 'Statut', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loadingBordereaux ? (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Chargement…</td></tr>
                  ) : bordereaux.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Aucun bordereau</td></tr>
                  ) : bordereaux.map(b => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{b.numero}</td>
                      <td className="px-3 py-2.5 text-gray-700">{b.periode}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{b.parcelle?.code || 'Toutes'}</td>
                      <td className="px-3 py-2.5 text-center">{b.nbBons}</td>
                      <td className="px-3 py-2.5 text-right font-medium">{fmt(b.totalPoidsNet)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(b.tauxUnitaire)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">
                        {fmtFCFA(b.totalPoidsNet * b.tauxUnitaire)}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUT_BORDEREAU_BADGE[b.statut] || 'bg-gray-100 text-gray-600'}`}>
                          {b.statut}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {b.statut === 'valide' && (
                          <button onClick={() => setBordereauAPayer(b)}
                            className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">
                            Payer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Rendements ── */}
      {activeTab === 'rendements' && (
        <div className="space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <KpiCard label="Total récolté" value={`${fmt(Math.round(totalRecolte / 1000))} t`}
              sub={`${fmt(totalRecolte)} kg`} icon={Leaf} color="bg-emerald-100 text-emerald-700" />
            <KpiCard label="Rendement moyen" value={`${moyRendement.toFixed(2)} t/ha`}
              icon={BarChart3} color="bg-blue-100 text-blue-700" />
            <KpiCard label="Taux objectif" value={`${tauxObjectif.toFixed(1)} %`}
              icon={Check} color="bg-amber-100 text-amber-700" />
          </div>

          {/* Contrôles */}
          <div className="flex flex-wrap gap-3 items-center">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Campagne</label>
              <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                value={campagneRendement} onChange={e => setCampagneRendement(e.target.value)}>
                <option value="">Toutes</option>
                {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Grouper par</label>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                {(['parcelle', 'bloc', 'secteur'] as const).map(g => (
                  <button key={g} onClick={() => setGroupBy(g)}
                    className={`px-3 py-1.5 text-sm capitalize transition-colors
                      ${groupBy === g ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={loadRendements}
              className="mt-4 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRendements ? 'animate-spin' : ''}`} /> Actualiser
            </button>
          </div>

          {/* Graphique */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Rendement (t/ha) par {groupBy}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} unit=" t/ha" />
                  <Tooltip formatter={(v: any) => [`${v} t/ha`]} />
                  <Bar dataKey="rendement" fill="#059669" radius={[4, 4, 0, 0]} name="Rendement" />
                  <Bar dataKey="objectif" fill="#d1fae5" radius={[4, 4, 0, 0]} name="Objectif" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    {['Unité', 'Culture', 'Surface (ha)', 'Récolte (kg)', 'Rendement (t/ha)', 'Objectif (t/ha)', 'Taux (%)'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loadingRendements ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">Chargement…</td></tr>
                  ) : rendements.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">Aucune donnée de rendement</td></tr>
                  ) : rendements.map(r => (
                    <tr key={r.parcelleId} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium text-gray-800">
                        {r.parcelle?.code || r.parcelleId.slice(0, 8)} — {r.parcelle?.nom || ''}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">
                        {r.parcelle ? CULTURE_LABEL[r.parcelle.typeCulture] || r.parcelle.typeCulture : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">{r.surfaceHa.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(r.totalKg)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{r.rendementTha.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{r.objectifTha?.toFixed(2) || '—'}</td>
                      <td className="px-3 py-2.5 text-right">
                        {r.tauxRealisationPct != null ? (
                          <span className={`font-medium ${r.tauxRealisationPct >= 100 ? 'text-emerald-600' : r.tauxRealisationPct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {r.tauxRealisationPct.toFixed(1)} %
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Véhicules ── */}
      {activeTab === 'vehicules' && (
        <VehiculesTab entiteId={entiteId} />
      )}

      {/* ── Tab Transport ── */}
      {activeTab === 'transport' && (
        <TransportTab
          entiteId={entiteId}
          campagnes={campagnes}
          vehicules={vehicules}
          campagneActive={campagnes.find(c => c.id === campagneActive)}
        />
      )}

      {/* ── Tab Sûreté ── */}
      {activeTab === 'surete' && (
        <SureteTab
          entiteId={entiteId}
          campagnes={campagnes}
          campagneActive={campagnes.find(c => c.id === campagneActive)}
        />
      )}

      {/* ── Modals ── */}
      {showModalParcelle && (
        <ModalParcelle
          parent={parentForParcelle}
          entiteId={entiteId}
          onClose={() => { setShowModalParcelle(false); setParentForParcelle(null); setEditingParcelle(null); }}
          onSaved={loadArbre}
          editing={editingParcelle}
        />
      )}
      {showModalBon && (
        <ModalBon
          entiteId={entiteId}
          campagnes={campagnes}
          arbre={arbre}
          onClose={() => setShowModalBon(false)}
          onSaved={loadBons}
        />
      )}
      {showModalBordereau && (
        <ModalBordereau
          entiteId={entiteId}
          campagnes={campagnes}
          arbre={arbre}
          onClose={() => setShowModalBordereau(false)}
          onSaved={loadBordereaux}
        />
      )}
      {bordereauAPayer && (
        <ModalPaiement
          bordereau={bordereauAPayer}
          onClose={() => setBordereauAPayer(null)}
          onSaved={loadBordereaux}
        />
      )}
    </div>
  );
};

export default PlantationPage;
